import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { emptyTableCellClassName } from '@/constants/ui'
import { Loader } from '@/components/ui/Loader'
import { StatusBadge } from '@/components/orders/StatusBadge'
import { formatMoscowDateTime, formatRub } from '@/utils/format'
import { profileName, companyName } from '@/utils/entities'
import { useIsMobile } from '@/hooks/useIsMobile'
import type { BillingRow } from '@/hooks/useBillingAggregation'
import type { Order } from '@/types/order'

function customerCompanyName(o: Order): string {
  if (o.customer_company?.name) return o.customer_company.name
  return companyName(o.customer_company_id)
}

function inspectorDisplay(o: Order): string {
  if (!o.inspector_id) return '—'
  if (o.inspector_profile?.full_name) return o.inspector_profile.full_name
  return profileName(o.inspector_id)
}

export function BillingTable({
  rows,
  isLoadingOrders,
}: {
  rows: BillingRow[]
  isLoadingOrders: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const mobile = useIsMobile()

  if (isLoadingOrders && rows.length === 0) {
    return <Loader centered className="py-8" />
  }
  if (!isLoadingOrders && rows.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-[var(--stryzh-color-text-secondary)]">
        {t('common.noData')}
      </p>
    )
  }

  if (mobile) {
    return (
      <div className="space-y-2">
        {rows.map((row) => (
          <BillingMobileCard
            key={row.order.id}
            row={row}
            onOpen={() => navigate(`/orders/${row.order.id}`)}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
      <table className="w-full min-w-[860px] border-collapse text-left text-sm">
        <thead className="sticky top-0 z-10 bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
          <tr>
            <th className="px-3 py-2 font-medium">{t('billing.col.completedAt')}</th>
            <th className="px-3 py-2 font-medium">{t('billing.col.car')}</th>
            <th className="px-3 py-2 font-medium">{t('billing.col.company')}</th>
            <th className="px-3 py-2 font-medium">{t('billing.col.inspector')}</th>
            <th className="px-3 py-2 font-medium">{t('billing.col.outcome')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('billing.col.forCustomer')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('billing.col.forInspector')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className={emptyTableCellClassName}>
                {t('common.noData')}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const { order, billing, pending, error } = row
              const archived = order.status === 'CANCELLED'
              return (
                <tr
                  key={order.id}
                  className={`cursor-pointer border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)] ${
                    archived
                      ? 'opacity-[var(--stryzh-opacity-archived)]'
                      : ''
                  }`}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td className="px-3 py-2 text-[color:var(--stryzh-color-text-primary)]">
                    {formatMoscowDateTime(order.status_updated_at)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[color:var(--stryzh-color-text-primary)]">
                      {order.brand} {order.model}
                    </div>
                    {order.vin ? (
                      <div className="font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                        {order.vin}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-[color:var(--stryzh-color-text-primary)]">
                    {customerCompanyName(order)}
                  </td>
                  <td className="px-3 py-2 text-[color:var(--stryzh-color-text-primary)]">
                    {inspectorDisplay(order)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-3 py-2 text-right font-[family-name:var(--stryzh-font-family-mono)] text-[color:var(--stryzh-color-text-primary)]">
                    {renderAmount(
                      billing?.totals.total_customer,
                      pending,
                      error,
                      t,
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-[family-name:var(--stryzh-font-family-mono)] text-[color:var(--stryzh-color-text-secondary)]">
                    {renderAmount(
                      billing?.totals.total_inspector,
                      pending,
                      error,
                      t,
                    )}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

function renderAmount(
  value: number | undefined,
  pending: boolean,
  error: string | null,
  t: TFunction,
) {
  if (error)
    return (
      <span
        title={error}
        className="text-[color:var(--stryzh-color-semantic-error)]"
      >
        {t('billing.amountError')}
      </span>
    )
  if (pending || value == null)
    return <span aria-label={t('billing.amountCalculating')}>…</span>
  return formatRub(value)
}

function BillingMobileCard({
  row,
  onOpen,
}: {
  row: BillingRow
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const { order, billing, pending, error } = row
  const archived = order.status === 'CANCELLED'
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`w-full rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-3 text-left ${
        archived ? 'opacity-[var(--stryzh-opacity-archived)]' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-[color:var(--stryzh-color-text-primary)]">
            {order.brand} {order.model}
          </div>
          <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {formatMoscowDateTime(order.status_updated_at)}
          </div>
        </div>
        <StatusBadge status={order.status} />
      </div>
      <div className="mt-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
        {customerCompanyName(order)} · {inspectorDisplay(order)}
      </div>
      <div className="mt-2 flex items-center justify-between font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-sm)]">
        <span className="text-[color:var(--stryzh-color-text-primary)]">
          {t('billing.card.customer')}{' '}
          {renderAmount(billing?.totals.total_customer, pending, error, t)}
        </span>
        <span className="text-[color:var(--stryzh-color-text-secondary)]">
          {t('billing.card.inspector')}{' '}
          {renderAmount(billing?.totals.total_inspector, pending, error, t)}
        </span>
      </div>
    </button>
  )
}
